import { makeStyles } from "tss-react/mui";
import { BUTTON_VARIANTS } from "@miplatform/constants";
import { CalendarEventType } from "@miplatform/types";
import { useDraggable } from "@dnd-kit/core";
import { mdiAccountPlusOutline } from "@mdi/js";
import { Box } from "@mui/material";
import { MiButton } from "../../MiButton/MiButton";
import { Bucket } from "../../MiScheduler/Bucket/Bucket";
import { IPrioritySchedulerBucketsProps } from "../PrioritySchedulerBoard.types";
import { getCardDnDId } from "../PrioritySchedulerBoard.utils";

type TrayData = NonNullable<
  NonNullable<IPrioritySchedulerBucketsProps["trays"]>[number]["data"]
>;

export function PrioritySchedulerBuckets({
  trays,
  highlightedEventsMap,
  onCardClick,
  EventComponent,
  indicatorWidth,
  followUpTrayFilter,
  workBaseTemplates = [],
  isAutoAssignLoading = false,
  isAutoAssignActive = false,
  isAssigneeMode = false,
  onFollowUpAutoAssign,
  eventType,
}: Readonly<IPrioritySchedulerBucketsProps>) {
  const { classes } = useStyles();

  const showFollowUpAutoAssign =
    !isAssigneeMode &&
    !!followUpTrayFilter &&
    !!onFollowUpAutoAssign &&
    workBaseTemplates.length > 0;

  const isFollowUpTray = (filter?: string) =>
    showFollowUpAutoAssign && filter === followUpTrayFilter;

  return (
    <Box className={classes.bucketContainer} minWidth={indicatorWidth}>
      {trays?.map((bucket) => (
        <Bucket
          key={bucket.id}
          bucket={{
            data: bucket.data,
            ...bucket,
          }}
          headerEndContent={
            isFollowUpTray(bucket.filter) ? (
              <AutoAssignButton
                headerColor={bucket.headerColor}
                headerBackgroundColor={bucket.headerBackgroundColor}
                disabled={isAutoAssignLoading || isAutoAssignActive}
                onClick={() => onFollowUpAutoAssign?.()}
              />
            ) : undefined
          }
        >
          <BucketCards
            trayId={bucket.id}
            onCardClick={onCardClick}
            highlightedEventsMap={highlightedEventsMap}
            EventComponent={EventComponent}
            data={bucket.data}
            eventType={eventType}
          />
        </Bucket>
      ))}
    </Box>
  );
}

function BucketCards({
  data,
  trayId,
  onCardClick,
  EventComponent,
  highlightedEventsMap,
  eventType,
}: Readonly<{
  trayId: string;
  data?: TrayData;
  highlightedEventsMap?: Record<string, boolean>;
  onCardClick: IPrioritySchedulerBucketsProps["onCardClick"];
  EventComponent: IPrioritySchedulerBucketsProps["EventComponent"];
  eventType?: CalendarEventType;
}>) {
  return (
    <Box display="flex" gap={1}>
      {data?.map((event: TrayData[number]) => (
        <DraggableTrayCard
          key={event.id}
          trayId={trayId}
          event={event}
          isHighlighted={highlightedEventsMap?.[event.id]}
          onCardClick={onCardClick}
          EventComponent={EventComponent}
          eventType={eventType}
        />
      ))}
    </Box>
  );
}

function DraggableTrayCard({
  trayId,
  event,
  isHighlighted,
  onCardClick,
  EventComponent,
  eventType,
}: Readonly<{
  trayId: string;
  event: TrayData[number];
  isHighlighted?: boolean;
  onCardClick: IPrioritySchedulerBucketsProps["onCardClick"];
  EventComponent: IPrioritySchedulerBucketsProps["EventComponent"];
  eventType?: CalendarEventType;
}>) {
  const isDragDisabled =
    !!eventType && eventType === CalendarEventType.WorkOrder;
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: getCardDnDId(event.id),
    disabled: isDragDisabled,
    data: {
      type: "card",
      cardId: event.id,
      container: { type: "tray", id: trayId },
    },
  });

  return (
    <Box
      ref={setNodeRef}
      flexShrink={0}
      sx={{ opacity: isDragging ? 0.45 : 1 }}
      {...attributes}
      {...listeners}
    >
      <EventComponent
        attempt={event}
        onCardClick={onCardClick}
        isHighlighted={isHighlighted}
        source={{ type: "tray", id: trayId }}
      />
    </Box>
  );
}

function AutoAssignButton({
  headerColor,
  headerBackgroundColor,
  disabled,
  onClick,
}: Readonly<{
  headerColor?: string;
  headerBackgroundColor?: string;
  disabled?: boolean;
  onClick: () => void;
}>) {
  const { classes } = useAutoAssignButtonStyles({
    headerColor,
    headerBackgroundColor,
  });

  return (
    <MiButton
      variant={BUTTON_VARIANTS.CONTAINED}
      title="Auto Assign"
      startIcon={{
        path: mdiAccountPlusOutline,
        iconColor: headerBackgroundColor,
      }}
      disabled={disabled}
      onClick={onClick}
      className={classes.autoAssignButton}
    />
  );
}

const useStyles = makeStyles()((theme) => ({
  bucketContainer: {
    display: "flex",
    flexDirection: "column",
    flexShrink: 0,
    backgroundColor: theme.palette.background.paper,
    zIndex: 3,
  },
}));

const useAutoAssignButtonStyles = makeStyles<{
  headerColor?: string;
  headerBackgroundColor?: string;
}>()((theme, { headerColor, headerBackgroundColor }) => ({
  autoAssignButton: {
    height: 24,
    padding: theme.spacing(2, 2),
    borderWidth: 1.5,
    backgroundColor: headerColor,
    color: headerBackgroundColor,
    "&:hover": {
      backgroundColor: headerColor,
      filter: "brightness(0.95)",
    },
  },
}));
